import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../config/api.js";
import { simpleHash } from "../utils/helpers.js";
import { BACKEND_URL } from "../config/supabase.js";
import { CustSession } from "../config/session.js";
import { BloomMark, Wordmark } from "../components/Icon.jsx";

// ─── CUSTOMER AUTH ────────────────────────────────────────────────────────────
export function CustomerAuth({ mode, setPage, onCustomerLogin }) {
  const navigate = useNavigate();
  const [form, setForm]   = useState({name:"",email:"",phone:"",instagram:"",pass:"",pass2:""});
  const [errs, setErrs]   = useState({});
  const [loading, setLoading] = useState(false);
  const [notice, setNotice] = useState(""); // friendly banner (e.g. nudging guests to sign up)
  const h = e => setForm(f=>({...f,[e.target.name]:e.target.value}));
  const Err = ({k})=>errs[k]?<p style={{fontSize:11,color:"var(--red)",marginTop:4}}>{errs[k]}</p>:null;

  // Strip +91 / leading 0 / spaces / dashes → bare digits for validation & storage.
  const normPhone = (v) => {
    let p = String(v).replace(/\D/g, "");
    if (p.length === 12 && p.startsWith("91")) p = p.slice(2);
    if (p.length === 11 && p.startsWith("0"))  p = p.slice(1);
    return p;
  };

  const submit = async () => {
    const e = {};
    if (mode==="signup") {
      if (!form.name.trim()) e.name = "Name is required";
      if (form.pass!==form.pass2) e.pass2 = "Passwords don't match";
      // Phone is optional here — but if entered it must be a real 10-digit Indian mobile.
      if (form.phone.trim() && !/^[6-9]\d{9}$/.test(normPhone(form.phone))) e.phone = "Enter a valid 10-digit mobile number";
    }
    if (!form.email.includes("@")) e.email = "Enter a valid email";
    if (form.pass.length<6) e.pass = "Password must be 6+ characters";
    if (Object.keys(e).length) { setErrs(e); return; }
    setLoading(true);
    try {
      if (mode==="signup") {
        let existing;
        try { existing = await api.findCustomerByEmail(form.email); }
        catch (fe) {
          setErrs({ email: fe.status===429
            ? "Too many attempts right now — please wait a minute and try again."
            : "Couldn't reach the server — please check your connection and try again." });
          setLoading(false); return;
        }
        if (existing.length>0) { setErrs({email:"Email already registered — try logging in"}); setLoading(false); return; }
        // Only include optional fields if they have values — avoids column-not-found errors
        const nc = {
          id: `c-${Date.now()}`,
          name: form.name.trim(),
          email: form.email.toLowerCase().trim(),
          password_hash: simpleHash(form.pass),
          ...(form.phone.trim() ? { phone:   normPhone(form.phone) }                          : {}),
          ...(form.instagram? { instagram: form.instagram.replace(/^@/,"").trim() || null } : {}),
        };
        const saved = await api.insertCustomer(nc);
        if (!saved) { setErrs({email:"Sign up failed — please try again"}); setLoading(false); return; }
        CustSession.save({ customerId:saved.id||nc.id });
        onCustomerLogin(saved||nc);
      } else {
        let rows;
        try { rows = await api.findCustomerByEmail(form.email); }
        catch (fe) {
          setErrs({ email: fe.status===429
            ? "Too many attempts right now — please wait a minute and try again."
            : "Couldn't reach the server — please check your connection and try again." });
          setLoading(false); return;
        }
        if (rows.length===0) {
          // Guest who bought without an account: they never registered, so there's
          // nothing to log into. Send them to sign up — keep the email so their past
          // guest orders (matched by email) appear once the account is created.
          setErrs({});
          setNotice("You haven't signed up yet. Create an account with this email and your past orders will show up here. ");
          setLoading(false);
          navigate("/customer/signup");
          return;
        }
        // The account exists — verify the password SERVER-SIDE. The password hash is
        // never sent to the browser (was: client fetched the hash and compared it here,
        // which exposed every customer's password hash by email). /api/auth/login is
        // rate-limited and returns a signed session token on success.
        let r, j;
        try {
          r = await fetch(`${BACKEND_URL}/api/auth/login`, {
            method: "POST", headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ email: form.email.toLowerCase().trim(), password: form.pass, role: "customer" }),
          });
          j = await r.json().catch(()=>({}));
        } catch { setErrs({ email: "Something went wrong — check your connection" }); setLoading(false); return; }
        if (r.status === 429) { setErrs({ pass: j.error || "Too many attempts — please wait a few minutes." }); setLoading(false); return; }
        if (!r.ok || !j.user) { setErrs({ pass: "Incorrect password" }); setLoading(false); return; }
        CustSession.save({ customerId: j.user.id, token: j.token });
        onCustomerLogin(j.user);
      }
    } catch(err) { console.error(err); setErrs({email:"Something went wrong — check your connection"}); setLoading(false); }
  };

  return (
    <div style={{minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",background:"var(--offwhite)",padding:"40px 16px"}}>
      {loading&&(
        <div style={{position:"fixed",inset:0,background:"var(--overlay)",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",zIndex:9999,gap:16}}>
          <div style={{width:48,height:48,border:"3px solid rgba(255,255,255,0.3)",borderTopColor:"#fff",borderRadius:"50%",animation:"spin 0.8s linear infinite"}}/>
          <p style={{color:"#fff",fontFamily:"Syne",fontWeight:600,fontSize:16}}>{mode==="login"?"Logging you in…":"Creating your account…"}</p>
        </div>
      )}
      <div className="card fadeUp" style={{width:"100%",maxWidth:420,padding:44}}>
        <div style={{textAlign:"center",marginBottom:28}}>
          <div style={{marginBottom:10}}><Wordmark size={40}/></div>
          <h2 style={{fontSize:26}}>{mode==="login"?"Welcome back":"Create your account"}</h2>
          <p style={{color:"var(--ink-soft)",marginTop:6,fontSize:13}}>
            {mode==="login"?"Log in to track your orders":"Sign up to see your past orders & shop across all Bloom stores"}
          </p>
        </div>
        {notice&&(
          <div style={{background:"var(--teal-light)",border:"1px solid var(--teal-mid)",borderRadius:4,padding:"12px 16px",marginBottom:18,fontSize:13,color:"var(--teal-deep)",fontWeight:600,lineHeight:1.45}}>
            {notice}
          </div>
        )}
        <div style={{display:"flex",flexDirection:"column",gap:13}}>
          {mode==="signup"&&<div><input className={`inp${errs.name?" err":""}`} name="name" placeholder="Your full name" value={form.name} onChange={h}/><Err k="name"/></div>}
          <div><input className={`inp${errs.email?" err":""}`} name="email" type="email" placeholder="Email address" value={form.email} onChange={h}/><Err k="email"/></div>
          {mode==="signup"&&<div><input className={`inp${errs.phone?" err":""}`} name="phone" type="tel" inputMode="numeric" autoComplete="tel" maxLength={15} placeholder="Phone number (optional)" value={form.phone} onChange={h}/><Err k="phone"/></div>}
          {mode==="signup"&&(
            <div style={{position:"relative"}}>
              <span style={{position:"absolute",left:14,top:"50%",transform:"translateY(-50%)",fontSize:13,color:"var(--ink-soft)",fontWeight:700,pointerEvents:"none"}}>@</span>
              <input className="inp" name="instagram" placeholder="Instagram handle (optional)" value={form.instagram||""} onChange={h} style={{paddingLeft:30}}/>
            </div>
          )}
          <div><input className={`inp${errs.pass?" err":""}`} name="pass" type="password" placeholder="Password (6+ characters)" value={form.pass} onChange={h}/><Err k="pass"/></div>
          {mode==="signup"&&<div><input className={`inp${errs.pass2?" err":""}`} name="pass2" type="password" placeholder="Confirm password" value={form.pass2} onChange={h}/><Err k="pass2"/></div>}
          <button className="btn-pink" style={{marginTop:4,padding:14,fontSize:14,borderRadius:4}} onClick={submit} disabled={loading}>
            {loading?(mode==="login"?"Logging in…":"Creating account…"):mode==="login"?"Log In":<>{"Create Account "}<BloomMark /></>}
          </button>
          {mode==="login"&&(
            <p style={{textAlign:"center",fontSize:12,color:"var(--ink-soft)",marginTop:4}}>
              <span style={{color:"var(--teal)",cursor:"pointer",fontWeight:600}} onClick={()=>navigate("/forgot-password?type=customer")}>Forgot password?</span>
            </p>
          )}
        </div>
        <p style={{textAlign:"center",marginTop:18,fontSize:13,color:"var(--ink-soft)"}}>
          {mode==="login"?"New here? ":"Already have an account? "}
          <span style={{color:"var(--pink-btn)",cursor:"pointer",fontWeight:600}} onClick={()=>{setErrs({});setNotice("");navigate(mode==="login"?"/customer/signup":"/customer/login");}}>
            {mode==="login"?"Create account":"Log in"}
          </span>
        </p>
        <p style={{textAlign:"center",marginTop:12,fontSize:12,color:"var(--ink-soft)"}}>
          Are you a business? <span style={{color:"var(--pink-btn)",cursor:"pointer",fontWeight:600}} onClick={()=>navigate("/login")}>Business Login →</span>
        </p>
        {/* Razorpay trust badge */}
        <div style={{display:"flex",alignItems:"center",justifyContent:"center",gap:7,marginTop:22,paddingTop:18,borderTop:"1px solid var(--border)"}}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" stroke="#3395FF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
          <span style={{fontSize:11,color:"var(--ink-soft)",letterSpacing:"0.02em"}}>Payments secured by</span>
          <span style={{fontSize:12,fontWeight:800,color:"#3395FF",letterSpacing:"-0.01em"}}>Razorpay</span>
        </div>
      </div>
    </div>
  );
}
